import { useState } from 'react';
import { BordButton } from 'bord-design-system';
import BordStatusBadge from 'components/NudosComponents/BordStatusBadge/BordStatusBadge';
import {
  IQuoteEditorFormData,
  ISuggestedToolData,
  IToolSearchResult,
  IQuotePricingConfig
} from 'types/quoteEditor.types';
import { IcountryPricingData } from 'types/products';
import { getProductDetail } from 'services/cataloge';
import QuoteNewToolForm from '../QuoteNewToolForm/QuoteNewToolForm';
import QuoteToolSearch from './components/QuoteToolSearch/QuoteToolSearch';
import QuoteSelectedTool from './components/QuoteSelectedTool/QuoteSelectedTool';
import { bordColorTheme } from '../../../../../styles/styles.constants';
import { PRODUCT_CATEGORY } from 'types/category';
import './QuoteSuggestedTool.scss';

const EMPTY_FORM_DATA: IQuoteEditorFormData = {
  minDeliveryEstimate: '',
  maxDeliveryEstimate: '',
  provider: '',
  costWithoutTax: '',
  margin: '',
  platinumPrice: '',
  finalPrice: '',
  finalPriceOverride: false,
  mpn: '',
  hasCTO: false
};

interface IQuoteSuggestedTool {
  suggestionIndex: number;
  data: ISuggestedToolData;
  canRemove: boolean;
  requestedUnits: number;
  countryCode: string;
  quoteItemId: number;
  pricingConfig: IQuotePricingConfig;
  isFormDisabled?: boolean;
  onChange: (data: ISuggestedToolData) => void;
  onRemove: () => void;
  onToolCreated: () => void;
}

/** Convierte un tiempo de entrega al equivalente en semanas según el período dado. */
const toDeliveryWeeks = (time: number, period: string): string => {
  if (period === 'weeks') return String(time);
  const weeks = Math.ceil(time / 7);
  return String(weeks > 0 ? weeks : 1);
};

/**
 * Construye el formData pre-cargado a partir de los datos de pricing del catálogo
 * para el país de la cotización. finalPrice se deriva de platinumUsd × planMultiplier.
 */
const buildFormDataFromProductDetail = (
  countryPricing: IcountryPricingData,
  tool: IToolSearchResult,
  pricingConfig: IQuotePricingConfig
): IQuoteEditorFormData => {
  const { estimatedDelivery } = tool;
  const { planMultiplier } = pricingConfig;
  const safePlanMultiplier = typeof planMultiplier === 'number' && isFinite(planMultiplier) ? planMultiplier : 1;
  const platinumUsd = parseFloat(String(countryPricing.nudosPriceUsdWithTaxes ?? 0)) || 0;
  const rawFinalPrice = safePlanMultiplier === 1 ? platinumUsd : Math.floor((platinumUsd * safePlanMultiplier) / 5) * 5;

  return {
    minDeliveryEstimate: estimatedDelivery ? toDeliveryWeeks(estimatedDelivery.minTime, estimatedDelivery.period) : '',
    maxDeliveryEstimate: estimatedDelivery ? toDeliveryWeeks(estimatedDelivery.maxTime, estimatedDelivery.period) : '',
    provider: '',
    providerId: countryPricing.providerId ?? undefined,
    costWithoutTax: countryPricing.providerPrice != null ? String(countryPricing.providerPrice) : '',
    margin: countryPricing.margin != null ? String(countryPricing.margin) : '',
    platinumPrice: platinumUsd > 0 ? String(platinumUsd) : '',
    finalPrice: platinumUsd > 0 ? String(rawFinalPrice) : '',
    finalPriceOverride: false,
    mpn: '',
    hasCTO: false
  };
};

/**
 * Panel de herramienta sugerida para tools genéricas.
 * Orquesta 3 estados excluyentes: búsqueda, creación de herramienta nueva y herramienta seleccionada.
 */
const QuoteSuggestedTool = ({
  suggestionIndex,
  data,
  canRemove,
  requestedUnits,
  countryCode,
  quoteItemId,
  pricingConfig,
  isFormDisabled,
  onChange,
  onRemove,
  onToolCreated
}: IQuoteSuggestedTool) => {
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isLoadingPricing, setIsLoadingPricing] = useState(false);
  const [isNewlyCreated, setIsNewlyCreated] = useState(false);

  const headerTitle = `HERRAMIENTA SUGERIDA ${suggestionIndex + 1}`;
  const isActiveInStore = data.selectedTool?.status === 'active';

  /**
   * Selecciona una herramienta. Si es un producto activo en tienda, obtiene los datos de pricing
   * del catálogo para el país de la cotización y pre-carga el formulario con ellos.
   */
  const handleSelectTool = async (tool: IToolSearchResult) => {
    if (tool.status !== 'active') {
      onChange({
        ...data,
        selectedTool: tool,
        formData: { ...EMPTY_FORM_DATA, hasCTO: tool.category === PRODUCT_CATEGORY.CTO_LAPTOPS }
      });
      return;
    }
    setIsLoadingPricing(true);
    try {
      const productDetail = await getProductDetail(tool.id);
      const countryPricing = productDetail?.pricingData?.find(
        p => p.nameCode?.toLowerCase() === pricingConfig.countryCode?.toLowerCase()
      );
      const formData = countryPricing
        ? buildFormDataFromProductDetail(countryPricing, tool, pricingConfig)
        : { ...EMPTY_FORM_DATA, hasCTO: tool?.category === PRODUCT_CATEGORY.CTO_LAPTOPS };
      onChange({ ...data, selectedTool: tool, formData });
    } catch {
      onChange({
        ...data,
        selectedTool: tool,
        formData: { ...EMPTY_FORM_DATA, hasCTO: tool?.category === PRODUCT_CATEGORY.CTO_LAPTOPS }
      });
    } finally {
      setIsLoadingPricing(false);
    }
  };

  const handleSavedNewTool = (_tool: IToolSearchResult) => {
    setIsCreatingNew(false);
    setIsNewlyCreated(true);
    onToolCreated();
  };

  const handleRemoveSelectedTool = () => {
    setIsNewlyCreated(false);
    onChange({ ...data, selectedTool: null, formData: { ...EMPTY_FORM_DATA } });
  };

  return (
    <div className="quoteSuggestedTool">
      <div className="suggestedHeader">
        <div className="suggestedHeaderLeft">
          {headerTitle}
          {isActiveInStore && (
            <BordStatusBadge
              label="Herramienta prendida en tienda"
              dotColor={bordColorTheme.success.default}
              textColor={bordColorTheme.navyBlue[900]}
            />
          )}
          {isNewlyCreated && !isActiveInStore && (
            <BordStatusBadge
              label="Herramienta nueva"
              dotColor={bordColorTheme.success.default}
              textColor={bordColorTheme.navyBlue[900]}
            />
          )}
        </div>
        {canRemove && !isFormDisabled && (
          <BordButton label="Eliminar" bordButtonStyle="outlined" onClick={onRemove} customClassName="removeBtn" />
        )}
      </div>
      <div className="suggestedBody">
        {!data.selectedTool && !isCreatingNew && !isFormDisabled && (
          <QuoteToolSearch
            countryCode={countryCode}
            onSelectTool={handleSelectTool}
            onCreateNew={() => setIsCreatingNew(true)}
          />
        )}
        {!data.selectedTool && isCreatingNew && !isFormDisabled && (
          <QuoteNewToolForm
            countryCode={countryCode}
            quoteItemId={quoteItemId}
            onSave={handleSavedNewTool}
            onCancel={() => setIsCreatingNew(false)}
          />
        )}
        {data.selectedTool && !isLoadingPricing && (
          <QuoteSelectedTool
            tool={data.selectedTool}
            formData={data.formData}
            requestedUnits={requestedUnits}
            pricingConfig={pricingConfig}
            isFormDisabled={isFormDisabled}
            onChangeTool={handleRemoveSelectedTool}
            onFormChange={formData => onChange({ ...data, formData })}
          />
        )}
        {data.selectedTool && isLoadingPricing && <div className="pricingLoader">Cargando datos de pricing...</div>}
      </div>
    </div>
  );
};

export default QuoteSuggestedTool;
